/* Lunéa PDP organizer — intentionally small: one-time layout enhancement + delegated controls. */
(() => {
  const PRODUCT_SELECTOR = 'product-info.lp-editorial';
  const PAYMENT_PROVIDERS = ['affirm', 'shop-pay', 'klarna'];

  if (customElements.get('lunea-pdp-organizer')) return;

  class LuneaPdpOrganizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.product = document.querySelector(PRODUCT_SELECTOR);
      this.shell = this.querySelector('[data-lp-payment-shell]');

      if (!this.product || !this.shell) {
        this.hidden = true;
        return;
      }

      this.drawer = this.shell.querySelector('.lp-payment-drawer');
      this.closeButton = this.shell.querySelector('.lp-payment-drawer__close');
      this.lastOpener = null;
      this.activeDetailsLabel = '';
      this.unsubscribers = [];

      // The installment form must remain inside product-info so Dawn can update its variant ID.
      this.product.appendChild(this.shell);

      const baseRequest = this.product.buildRequestUrlWithParams.bind(this.product);
      this.product.buildRequestUrlWithParams = (...args) => {
        const url = new URL(baseRequest(...args), location.origin);
        url.searchParams.set('view', 'lunea-v2');
        return url.pathname + url.search;
      };
      const baseUpdateURL = this.product.updateURL.bind(this.product);
      this.product.updateURL = (...args) => {
        baseUpdateURL(...args);
        const url = new URL(location.href);
        url.searchParams.set('view', 'lunea-v2');
        history.replaceState({}, '', url.pathname + url.search + url.hash);
      };
      this.patchVariantSelectorTransition();
      this.organizePayments();
      this.organizePurchase();
      this.organizeAssurance();
      this.enhanceSelects();
      this.enhanceDetailsTabs();

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onChange = this.onChange.bind(this);
      this.product.addEventListener('click', this.onClick);
      this.product.addEventListener('keydown', this.onKeydown);
      this.product.addEventListener('change', this.onChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribers.push(
          subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (String(data?.sectionId || '') !== String(this.product.dataset.section || '')) return;
            // The new selector was inserted directly by the patched Dawn method.
            requestAnimationFrame(() => {
              this.enhanceSelects();
              this.syncPaymentTrigger();
            });
          })
        );
      }
    }

    disconnectedCallback() {
      this.product?.removeEventListener('click', this.onClick);
      this.product?.removeEventListener('keydown', this.onKeydown);
      this.product?.removeEventListener('change', this.onChange);
      this.unsubscribers.forEach((unsubscribe) => unsubscribe?.());
      this.closeDrawer(false);
      if (this.shell?.parentElement === this.product) this.shell.remove();
    }

    patchVariantSelectorTransition() {
      if (this.product.dataset.lpFastVariantPicker === 'true') return;
      this.product.dataset.lpFastVariantPicker = 'true';

      // Dawn normally double-buffers this node for 500ms. A direct replacement keeps
      // the exact same variant logic but removes the visible "page refresh" effect.
      this.product.updateOptionValues = (html) => {
        const incoming = html.querySelector('variant-selects');
        const current = this.product.querySelector('variant-selects');
        if (!incoming || !current) return;
        current.replaceWith(incoming);
      };
    }

    organizePayments() {
      const payment = this.product.querySelector('.lp-editorial__payments');
      if (!payment) return;

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'lp-payment-trigger';
      trigger.dataset.lpPaymentOpen = '';
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', this.drawer?.id || '');
      trigger.innerHTML = `
        <span class="lp-payment-trigger__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.55">
            <rect x="3.5" y="5.5" width="17" height="13" rx="2"></rect>
            <path d="M3.5 9.2h17M7 14.3h3.2"></path>
          </svg>
        </span>
        <span class="lp-payment-trigger__content">
          <strong class="lp-payment-trigger__title">Flexible payment options</strong>
          <span class="lp-payment-trigger__brands" aria-label="Shop Pay, Affirm and Klarna">
            <span class="lp-payment-mini-brand lp-payment-mini-brand--shop-pay">Shop Pay</span>
            <span class="lp-payment-mini-brand lp-payment-mini-brand--affirm">Affirm</span>
            <span class="lp-payment-mini-brand lp-payment-mini-brand--klarna">Klarna</span>
          </span>
        </span>
        <span class="lp-payment-trigger__arrow" aria-hidden="true">›</span>
      `;
      payment.before(trigger);
      this.paymentTrigger = trigger;

      const body = payment.querySelector('.lp-editorial__payment-body');
      const children = body ? [...body.children] : [];
      const slots = Object.fromEntries(
        PAYMENT_PROVIDERS.map((provider) => [
          provider,
          this.shell.querySelector(`[data-lp-payment-slot="${provider}"]`),
        ])
      );
      const unknown = [];

      children.forEach((child) => {
        const signature = `${child.id || ''} ${child.className || ''} ${child.outerHTML || ''}`.toLowerCase();
        let provider = '';
        if (child.matches('form.installment') || child.querySelector('shopify-payment-terms')) provider = 'shop-pay';
        else if (signature.includes('affirm')) provider = 'affirm';
        else if (signature.includes('klarna')) provider = 'klarna';

        if (provider && slots[provider]) {
          child.dataset.lpPaymentProvider = provider;
          slots[provider].appendChild(child);
        } else {
          unknown.push(child);
        }
      });

      // The product template renders app blocks in Affirm → Klarna order. This
      // fallback is only used when an app wrapper has no identifiable class or ID.
      unknown.forEach((child, index) => {
        const provider = index === 0 ? 'affirm' : 'klarna';
        child.dataset.lpPaymentProvider = provider;
        slots[provider]?.appendChild(child);
      });

      this.initialPaymentHidden = payment.hidden;
      payment.remove();
      this.syncPaymentTrigger();
      this.selectPaymentTab('affirm', false);
    }

    syncPaymentTrigger() {
      if (!this.paymentTrigger) return;
      const pending = this.product.hasAttribute('data-lp-selection-pending');
      const variantId = this.product.querySelector('input.product-variant-id')?.value;
      const hidden = this.initialPaymentHidden || pending || !variantId;
      this.paymentTrigger.hidden = hidden;
      this.paymentTrigger.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      if (hidden && this.shell.classList.contains('is-open')) this.closeDrawer(false);
    }

    organizePurchase() {
      const info = this.product.querySelector('.product__info-container');
      if (!info || info.querySelector('.lp-purchase-card')) return;
      const start = info.querySelector('[id^="Inventory-"]');
      const end = info.querySelector('.lp-editorial__buy');
      if (!start || !end || start.parentNode !== end.parentNode) return;

      const card = document.createElement('div');
      card.className = 'lp-purchase-card';
      start.before(card);

      let node = start;
      while (node) {
        const next = node.nextSibling;
        card.appendChild(node);
        if (node === end) break;
        node = next;
      }
    }

    organizeAssurance() {
      const trust = this.product.querySelector('.lp-editorial__trust');
      if (!trust) return;
      const page = this.product.querySelector(':scope > .page-width');
      const details = page?.querySelector('lunea-pdp-sync');
      if (!page || !details) return;

      const section = document.createElement('section');
      section.className = 'lp-assurance-section';
      section.setAttribute('aria-labelledby', `LuneaAssuranceHeading-${this.product.dataset.section || 'product'}`);
      section.innerHTML = `
        <div class="lp-assurance-section__heading">
          <div>
            <p>Purchase with confidence</p>
            <h2 id="LuneaAssuranceHeading-${this.product.dataset.section || 'product'}">Care that continues after checkout</h2>
          </div>
        </div>
      `;
      section.appendChild(trust);

      const policyLinks = this.product.querySelector('.lp-editorial__policy-links');
      if (policyLinks) section.appendChild(policyLinks);
      details.before(section);
    }

    enhanceSelects() {
      this.product
        .querySelectorAll('.product-form__input--dropdown .select__select:not([data-lp-light-select])')
        .forEach((select) => this.enhanceSelect(select));
    }

    enhanceSelect(select) {
      const wrapper = select.closest('.select');
      if (!wrapper) return;
      select.dataset.lpLightSelect = 'true';
      select.classList.add('lp-light-select__native');
      select.tabIndex = -1;
      wrapper.classList.add('lp-light-select');

      const index = [...this.product.querySelectorAll('.select__select')].indexOf(select);
      const baseId = select.id || `LuneaOption-${this.product.dataset.section || 'product'}-${index}`;
      const label = select.closest('.product-form__input--dropdown')?.querySelector('.form__label');
      if (label) label.id ||= `${baseId}-label`;

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.id = `${baseId}-button`;
      trigger.className = 'lp-light-select__trigger';
      trigger.dataset.lpSelectTrigger = '';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', `${baseId}-menu`);
      if (label?.id) trigger.setAttribute('aria-labelledby', `${label.id} ${trigger.id}`);
      trigger.innerHTML = `
        <span class="lp-light-select__value" data-lp-select-value></span>
        <span class="lp-light-select__price" data-lp-select-price></span>
        <span class="lp-light-select__caret" aria-hidden="true"></span>
      `;

      const menu = document.createElement('div');
      menu.id = `${baseId}-menu`;
      menu.className = 'lp-light-select__menu';
      menu.dataset.lpSelectMenu = '';
      menu.setAttribute('role', 'listbox');
      if (label?.id) menu.setAttribute('aria-labelledby', label.id);
      menu.hidden = true;

      [...select.options].forEach((option, optionIndex) => {
        const parsed = this.parseOption(option.textContent);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lp-light-select__option';
        button.dataset.lpSelectOption = String(optionIndex);
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', optionIndex === select.selectedIndex ? 'true' : 'false');
        button.disabled = option.disabled || parsed.unavailable;
        button.innerHTML = `
          <span class="lp-light-select__option-value"></span>
          <span class="lp-light-select__option-price"></span>
        `;
        button.querySelector('.lp-light-select__option-value').textContent = parsed.value;
        const price = button.querySelector('.lp-light-select__option-price');
        price.textContent = parsed.price;
        price.hidden = !parsed.price;
        menu.appendChild(button);
      });

      select.after(trigger, menu);
      this.syncSelect(wrapper);
    }

    parseOption(text) {
      const parts = String(text || '')
        .split(/\s+—\s+/)
        .map((part) => part.trim())
        .filter(Boolean);
      const unavailable = parts.some((part) => /unavailable|sold\s*out/i.test(part));
      const price =
        parts.slice(1).find((part) => /[$€£¥₹]|(?:USD|CAD|AUD|EUR|GBP)\b/i.test(part) && !/unavailable/i.test(part)) ||
        '';
      return { value: parts[0] || '', price, unavailable };
    }

    syncSelect(wrapper) {
      const select = wrapper?.querySelector('select');
      const trigger = wrapper?.querySelector('[data-lp-select-trigger]');
      const menu = wrapper?.querySelector('[data-lp-select-menu]');
      if (!select || !trigger || !menu) return;
      const option = select.options[select.selectedIndex] || select.options[0];
      if (!option) return;
      const parsed = this.parseOption(option.textContent);
      trigger.querySelector('[data-lp-select-value]').textContent = parsed.value;
      const price = trigger.querySelector('[data-lp-select-price]');
      price.textContent = parsed.price;
      price.hidden = !parsed.price;
      trigger.disabled = select.disabled;
      menu.querySelectorAll('[data-lp-select-option]').forEach((button, index) => {
        button.setAttribute('aria-selected', index === select.selectedIndex ? 'true' : 'false');
      });
    }

    openSelect(wrapper, focus = true) {
      this.closeSelectMenus(wrapper);
      const trigger = wrapper.querySelector('[data-lp-select-trigger]');
      const menu = wrapper.querySelector('[data-lp-select-menu]');
      if (!trigger || !menu) return;
      wrapper.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      menu.hidden = false;
      if (focus) {
        requestAnimationFrame(() => {
          const selected = menu.querySelector('[aria-selected="true"]:not(:disabled)');
          (selected || menu.querySelector('[data-lp-select-option]:not(:disabled)'))?.focus();
        });
      }
    }

    closeSelect(wrapper, restoreFocus = false) {
      const trigger = wrapper?.querySelector('[data-lp-select-trigger]');
      const menu = wrapper?.querySelector('[data-lp-select-menu]');
      if (!trigger || !menu) return;
      wrapper.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
      if (restoreFocus) trigger.focus();
    }

    closeSelectMenus(except = null) {
      this.product.querySelectorAll('.lp-light-select.is-open').forEach((wrapper) => {
        if (wrapper !== except) this.closeSelect(wrapper);
      });
    }

    chooseSelectOption(button) {
      if (!button || button.disabled) return;
      const wrapper = button.closest('.lp-light-select');
      const select = wrapper?.querySelector('select');
      const index = Number(button.dataset.lpSelectOption);
      const nativeOption = select?.options[index];
      if (!select || !nativeOption || nativeOption.disabled) return;

      select.selectedIndex = index;
      [...select.options].forEach((option, optionIndex) => {
        if (optionIndex === index) option.setAttribute('selected', 'selected');
        else option.removeAttribute('selected');
      });
      this.syncSelect(wrapper);
      this.closeSelect(wrapper, true);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    enhanceDetailsTabs() {
      const description = this.product.querySelector('.lp-editorial__details .lp-desc');
      if (!description || description.dataset.lpDetailsTabs === 'true') return;
      const panels = [...description.querySelectorAll(':scope > details.lp-block')];
      if (panels.length < 2) return;

      description.dataset.lpDetailsTabs = 'true';
      description.classList.add('lp-details-tabs-ready');
      const tabs = document.createElement('div');
      tabs.className = 'lp-details-tabs';
      tabs.setAttribute('role', 'tablist');
      tabs.setAttribute('aria-label', 'Product details');

      panels.forEach((panel, index) => {
        const label = panel.querySelector(':scope > summary')?.textContent?.trim() || `Details ${index + 1}`;
        const tabId = `LuneaDetailsTab-${this.product.dataset.section || 'product'}-${index}`;
        const panelId = `LuneaDetailsPanel-${this.product.dataset.section || 'product'}-${index}`;
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.id = tabId;
        tab.className = 'lp-details-tab';
        tab.dataset.lpDetailsTab = String(index);
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panelId);
        tab.textContent = label;
        tabs.appendChild(tab);

        panel.id = panelId;
        panel.open = true;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabId);
      });

      panels[0].before(tabs);
      const selected = Math.max(
        0,
        panels.findIndex((panel) => panel.querySelector('summary')?.textContent?.trim() === this.activeDetailsLabel)
      );
      this.selectDetailsTab(description, selected, false);
    }

    selectDetailsTab(description, index, focus = false) {
      const tabs = [...description.querySelectorAll('[data-lp-details-tab]')];
      const panels = [...description.querySelectorAll(':scope > details.lp-block')];
      if (!tabs.length || !panels.length) return;
      const safeIndex = Math.min(Math.max(index, 0), tabs.length - 1);
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === safeIndex;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== safeIndex;
      });
      this.activeDetailsLabel = tabs[safeIndex].textContent.trim();
      if (focus) tabs[safeIndex].focus();
    }

    selectPaymentTab(provider, focus = false) {
      const tabs = [...this.shell.querySelectorAll('[data-lp-payment-tab]')];
      const panels = [...this.shell.querySelectorAll('[data-lp-payment-panel]')];
      tabs.forEach((tab) => {
        const selected = tab.dataset.lpPaymentTab === provider;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focus) tab.focus();
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.lpPaymentPanel !== provider;
      });
    }

    openDrawer(opener) {
      this.lastOpener = opener;
      this.shell.classList.add('is-open');
      this.shell.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lp-payment-lock');
      requestAnimationFrame(() => this.closeButton?.focus());
    }

    closeDrawer(restoreFocus = true) {
      if (!this.shell) return;
      this.shell.classList.remove('is-open');
      this.shell.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lp-payment-lock');
      if (restoreFocus) this.lastOpener?.focus();
    }

    onClick(event) {
      const detailsJump = event.target.closest('[data-lp-details-jump]');
      if (detailsJump) {
        const description = this.product.querySelector('.lp-editorial__details .lp-desc');
        if (description) {
          this.selectDetailsTab(description, 0);
          const firstPanel = description.querySelector(':scope > details.lp-block');
          if (firstPanel) firstPanel.open = true;
        }
        return; // Preserve the anchor's native jump and keyboard focus.
      }

      const paymentOpen = event.target.closest('[data-lp-payment-open]');
      if (paymentOpen) {
        event.preventDefault();
        this.openDrawer(paymentOpen);
        return;
      }

      if (event.target.closest('[data-lp-payment-close]')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const paymentTab = event.target.closest('[data-lp-payment-tab]');
      if (paymentTab) {
        event.preventDefault();
        this.selectPaymentTab(paymentTab.dataset.lpPaymentTab);
        return;
      }

      const selectTrigger = event.target.closest('[data-lp-select-trigger]');
      if (selectTrigger) {
        event.preventDefault();
        const wrapper = selectTrigger.closest('.lp-light-select');
        wrapper.classList.contains('is-open') ? this.closeSelect(wrapper) : this.openSelect(wrapper, false);
        return;
      }

      const selectOption = event.target.closest('[data-lp-select-option]');
      if (selectOption) {
        event.preventDefault();
        this.chooseSelectOption(selectOption);
        return;
      }

      const detailsTab = event.target.closest('[data-lp-details-tab]');
      if (detailsTab) {
        event.preventDefault();
        const description = detailsTab.closest('.lp-desc');
        this.selectDetailsTab(description, Number(detailsTab.dataset.lpDetailsTab));
        return;
      }

      if (!event.target.closest('.lp-light-select')) this.closeSelectMenus();
    }

    onChange(event) {
      if (event.target.matches('.lp-light-select__native')) this.syncSelect(event.target.closest('.lp-light-select'));
      if (event.target.matches('input.product-variant-id')) this.syncPaymentTrigger();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        const openSelect = this.product.querySelector('.lp-light-select.is-open');
        if (openSelect) {
          event.preventDefault();
          this.closeSelect(openSelect, true);
          return;
        }
        if (this.shell.classList.contains('is-open')) {
          event.preventDefault();
          this.closeDrawer();
          return;
        }
      }

      const selectTrigger = event.target.closest('[data-lp-select-trigger]');
      if (selectTrigger && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        const wrapper = selectTrigger.closest('.lp-light-select');
        this.openSelect(wrapper, true);
        if (event.key === 'ArrowUp') {
          requestAnimationFrame(() => {
            const enabled = [...wrapper.querySelectorAll('[data-lp-select-option]:not(:disabled)')];
            enabled.at(-1)?.focus();
          });
        }
        return;
      }

      const selectOption = event.target.closest('[data-lp-select-option]');
      if (selectOption) {
        const wrapper = selectOption.closest('.lp-light-select');
        const enabled = [...wrapper.querySelectorAll('[data-lp-select-option]:not(:disabled)')];
        const index = Math.max(0, enabled.indexOf(selectOption));
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.chooseSelectOption(selectOption);
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowDown') next = (index + 1) % enabled.length;
          if (event.key === 'ArrowUp') next = (index - 1 + enabled.length) % enabled.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = enabled.length - 1;
          enabled[next]?.focus();
        } else if (event.key === 'Tab') {
          this.closeSelect(wrapper);
        }
        return;
      }

      const paymentTab = event.target.closest('[data-lp-payment-tab]');
      if (paymentTab && ['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
        const tabs = [...this.shell.querySelectorAll('[data-lp-payment-tab]')];
        const index = tabs.indexOf(paymentTab);
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        event.preventDefault();
        this.selectPaymentTab(tabs[next].dataset.lpPaymentTab, true);
        return;
      }

      const detailsTab = event.target.closest('[data-lp-details-tab]');
      if (detailsTab && ['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
        const description = detailsTab.closest('.lp-desc');
        const tabs = [...description.querySelectorAll('[data-lp-details-tab]')];
        const index = tabs.indexOf(detailsTab);
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        event.preventDefault();
        this.selectDetailsTab(description, next, true);
        return;
      }

      if (event.key === 'Tab' && this.shell.classList.contains('is-open')) this.trapDrawerFocus(event);
    }

    trapDrawerFocus(event) {
      const focusable = [...this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.closest('[hidden]') && element.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('lunea-pdp-organizer', LuneaPdpOrganizer);
})();
